import { Link } from 'react-router-dom'
import type { ColDef, ValueFormatterParams } from 'ag-grid-community'
import { isCustomsAnswerStale, type CustomsQueueRow } from '@crm/shared'
import { CheckboxSetFilter } from '../table/CheckboxSetFilter'
import { Badge, CustomsStatusBadge } from '../../shared/ui/Badge'
import { DatasheetCell } from './components/DatasheetCell'
import { answerEditProps, type CustomsEditHandler } from './customsEditing'
import { formatRuDate, znakOf, type CustomsGridRow } from './customsGridRow'

export { formatRuDate, toCustomsGridRow, type CustomsGridRow } from './customsGridRow'

type CellProps = { data?: CustomsGridRow }

const dash = (p: ValueFormatterParams) =>
  p.value == null || p.value === '' ? '—' : String(p.value)

const textCol = (
  field: 'nonTariffNote' | 'certificateNote' | 'featuresNote',
  headerName: string,
  onEdit: CustomsEditHandler | undefined,
): ColDef<CustomsGridRow> => ({
  field,
  headerName,
  minWidth: 180,
  wrapText: true,
  autoHeight: true,
  cellClass: 'text-gray-700 leading-snug py-1',
  valueFormatter: dash,
  tooltipField: field,
  ...answerEditProps(field, onEdit),
})

/**
 * Партномер и пометка «менеджер изменил партномер» (с 30.10.2026, просьба Сергея):
 * до отправки брокеру — «было: …», после — «брокеру ушёл: …».
 */
export function PartNumberCell({ data }: { data: CustomsGridRow }) {
  const change = data.raw.partChange
  return (
    <div>
      <div className="font-mono">{data.partNumber}</div>
      {change && (
        <div
          className="text-[11px] text-amber-700"
          title={
            change.stage === 'AFTER_BROKER'
              ? 'Менеджер изменил партномер после отправки брокеру — ответ брокера по прежнему партномеру'
              : 'Менеджер изменил партномер после отправки логисту'
          }
        >
          {change.stage === 'AFTER_BROKER' ? 'Изменён, брокеру ушёл: ' : 'Изменён, было: '}
          <span className="font-mono">{change.from}</span>
        </div>
      )}
    </div>
  )
}

/** Значение прошлой проверки партномера: серым, с датой — своего ответа ещё нет. */
function ArchiveValue({ data, value }: { data: CustomsGridRow; value: string }) {
  const date = formatRuDate(data.raw.hint?.lastCheckedAt)
  return (
    <span className="text-gray-400" title={`Из архива от ${date}`}>
      {value} <span className="text-[11px]">(архив {date})</span>
    </span>
  )
}

export const customsDefaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  suppressMovable: false,
  filter: CheckboxSetFilter,
  floatingFilter: false,
}

export function buildCustomsColDefs(opts: {
  canEdit: boolean
  onOpenComments: (row: CustomsQueueRow) => void
  /** Правка ячеек ответа (только логист); без него колонки только читаются. */
  onEditAnswer?: CustomsEditHandler
}): ColDef<CustomsGridRow>[] {
  const { canEdit, onOpenComments } = opts
  const onEdit = canEdit ? opts.onEditAnswer : undefined
  return [
    {
      field: 'requestLabel',
      headerName: 'Заявка',
      minWidth: 150,
      comparator: (_a, _b, nodeA, nodeB) =>
        (nodeA.data?.raw.requestCreatedAt ?? '').localeCompare(
          nodeB.data?.raw.requestCreatedAt ?? '',
        ),
      cellRenderer: ({ data }: CellProps) =>
        data ? (
          <span>
            <span className="font-medium text-gray-900">{data.raw.requestNumber ?? '—'}</span>
            {data.raw.clientCode && (
              <span className="ml-1.5 font-mono text-xs text-gray-500">{data.raw.clientCode}</span>
            )}
          </span>
        ) : null,
    },
    { field: 'managerName', headerName: 'Менеджер', minWidth: 140, valueFormatter: dash },
    {
      field: 'sentBy',
      headerName: 'Прислал',
      headerTooltip: 'Когда и кто из продаж отправил позицию логисту кнопкой «Отправить брокеру»',
      minWidth: 170,
      valueFormatter: dash,
      comparator: (_a, _b, nodeA, nodeB) =>
        (nodeA.data?.raw.check?.sentToLogistAt ?? '').localeCompare(
          nodeB.data?.raw.check?.sentToLogistAt ?? '',
        ),
    },
    {
      field: 'partNumber',
      headerName: 'Партномер',
      minWidth: 160,
      wrapText: true,
      autoHeight: true,
      cellClass: 'leading-snug py-1',
      cellRenderer: (p: CellProps) => (p.data ? <PartNumberCell data={p.data} /> : null),
    },
    { field: 'brand', headerName: 'Производитель', minWidth: 130, valueFormatter: dash },
    { field: 'category', headerName: 'Наименование', minWidth: 140, valueFormatter: dash },
    {
      field: 'quantity',
      headerName: 'Кол-во',
      minWidth: 90,
      filter: false,
      cellClass: 'text-right tabular-nums',
      headerClass: 'ag-right-aligned-header',
      valueFormatter: (p: ValueFormatterParams) =>
        p.value == null ? '—' : `${Number(p.value).toLocaleString('ru-RU')}`,
    },
    { field: 'matrix', headerName: 'Матрица', minWidth: 120, valueFormatter: dash },
    {
      field: 'datasheet',
      headerName: 'Даташит',
      minWidth: 110,
      // Фильтр — «есть / пусто», а копирование (Ctrl+C) — сама ссылка.
      valueFormatter: ({ data }: ValueFormatterParams<CustomsGridRow>) =>
        data?.raw.datasheetUrl ?? '',
      cellRenderer: ({ data }: CellProps) =>
        data ? (
          <DatasheetCell
            itemId={data.id}
            partNumber={data.partNumber}
            url={data.raw.datasheetUrl}
            canEdit={canEdit}
          />
        ) : null,
    },
    {
      field: 'statusLabel',
      headerName: 'Статус',
      minWidth: 170,
      wrapText: true,
      autoHeight: true,
      cellRenderer: ({ data }: CellProps) =>
        data ? (
          <span title={data.raw.check?.statusNote ?? undefined} className="leading-snug">
            <CustomsStatusBadge status={data.raw.status} />
            {data.raw.status === 'NEEDS_INFO' && data.raw.check?.statusNote && (
              <span className="ml-1 text-xs text-gray-400">ⓘ</span>
            )}
            {/* Ответа ещё нет, но партномер уже проверяли: логист решает — оставить прошлый ответ или спросить брокера. */}
            {data.raw.status !== 'ANSWERED' && data.raw.hint && (
              <span
                className="block text-[11px] text-amber-700"
                title="Значения прошлой проверки — серым в колонках ответа. Оставить их — «Оставить прошлый ответ», иначе отправьте брокеру заново"
              >
                прошлый ответ от {formatRuDate(data.raw.hint.lastCheckedAt)}
                {isCustomsAnswerStale(data.raw.hint.lastCheckedAt) && ' · старше месяца'}
              </span>
            )}
          </span>
        ) : null,
    },
    {
      field: 'tnved',
      headerName: 'ТН ВЭД',
      minWidth: 150,
      wrapText: true,
      autoHeight: true,
      ...answerEditProps('tnved', onEdit),
      cellRenderer: ({ data }: CellProps) => {
        if (!data?.tnved) return <span className="text-gray-400">—</span>
        if (data.raw.check?.tnvedCode) return <span className="font-mono">{data.tnved}</span>
        const date = formatRuDate(data.raw.hint?.lastCheckedAt)
        return (
          <span className="text-gray-400 leading-snug" title={`Из архива от ${date}`}>
            <span className="font-mono">{data.tnved}</span>{' '}
            <span className="text-[11px]">из архива от {date}</span>
          </span>
        )
      },
    },
    {
      field: 'duty',
      headerName: 'Пошлина',
      minWidth: 110,
      ...answerEditProps('duty', onEdit),
      cellRenderer: ({ data }: CellProps) => {
        if (!data?.duty) return <span className="text-gray-400">—</span>
        if (data.raw.check?.dutyRate != null) return <span>{data.duty}</span>
        const date = formatRuDate(data.raw.hint?.lastCheckedAt)
        return (
          <span
            className="text-gray-400"
            title={`Прошлая проверка от ${date} — справочно, пошлину перепроверить у брокера`}
          >
            {data.duty} <span className="text-[11px]">на {date}</span>
          </span>
        )
      },
    },
    {
      field: 'fee',
      headerName: 'Сбор',
      minWidth: 150,
      ...answerEditProps('fee', onEdit),
      cellRenderer: ({ data }: CellProps) => {
        if (!data?.fee) return <span className="text-gray-400">—</span>
        if (data.raw.check?.customsFeeRaised == null)
          return <ArchiveValue data={data} value={data.fee} />
        return data.raw.check?.customsFeeRaised ? (
          <span className="text-red-600 font-medium">{data.fee}</span>
        ) : (
          <span>{data.fee}</span>
        )
      },
    },
    {
      field: 'znak',
      headerName: 'Честный знак',
      minWidth: 140,
      // Фильтр — три значения (поле znak), копирование — полный текст с датой.
      valueFormatter: ({ data }: ValueFormatterParams<CustomsGridRow>) => data?.znakText ?? '',
      ...answerEditProps('znak', onEdit),
      cellRenderer: ({ data }: CellProps) => {
        if (!data) return null
        const z = znakOf(data.raw)
        if (!z) return <span className="text-gray-400">—</span>
        if (!z.fromArchive) {
          return (
            <span className={z.value === 'UNKNOWN' ? 'text-gray-500' : undefined}>
              {data.znakText}
            </span>
          )
        }
        return (
          <span
            className="text-gray-400"
            title={`Из архива от ${formatRuDate(data.raw.hint?.lastCheckedAt)}`}
          >
            {data.znakText} <span className="text-[11px]">(архив)</span>
          </span>
        )
      },
    },
    {
      ...textCol('nonTariffNote', 'Нетарифка', onEdit),
      cellRenderer: ({ data }: CellProps) => {
        if (!data?.nonTariffNote) return <span className="text-gray-400">—</span>
        if (data.raw.check?.nonTariffNote) return <span>{data.nonTariffNote}</span>
        return <ArchiveValue data={data} value={data.nonTariffNote} />
      },
    },
    textCol('certificateNote', 'Сертификат', onEdit),
    textCol('featuresNote', 'Особенности', onEdit),
    {
      field: 'attention',
      headerName: 'Внимание',
      minWidth: 150,
      wrapText: true,
      autoHeight: true,
      cellRenderer: ({ data }: CellProps) =>
        data?.attention ? (
          <span title={data.attention}>
            <Badge color="red">{data.attention}</Badge>
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      field: 'checkedAt',
      headerName: 'Проверено',
      minWidth: 150,
      filterParams: { dateMode: true },
      comparator: (_a, _b, nodeA, nodeB) =>
        (nodeA.data?.raw.check?.checkedAt ?? '').localeCompare(
          nodeB.data?.raw.check?.checkedAt ?? '',
        ),
      cellRenderer: ({ data }: CellProps) => {
        if (!data?.checkedAt) return <span className="text-gray-400">—</span>
        const who = data.raw.check?.checkedBy?.name
        return (
          <span>
            {data.checkedAt}
            {who && <span className="ml-1 text-xs text-gray-500">· {who}</span>}
          </span>
        )
      },
    },
    { field: 'source', headerName: 'Источник', minWidth: 120, valueFormatter: dash },
    {
      field: 'brokerFileCode',
      headerName: 'Запрос брокеру',
      minWidth: 170,
      cellRenderer: ({ data }: CellProps) => {
        const br = data?.raw.check?.brokerRequest
        if (!br) return <span className="text-gray-400">—</span>
        return (
          <Link
            to={`/customs/requests/${br.id}`}
            className="font-mono text-brand-600 hover:text-brand-700 font-medium"
          >
            {br.fileCode}
          </Link>
        )
      },
    },
    {
      field: 'commentsCount',
      headerName: '💬',
      headerTooltip: 'Комментарии к позиции',
      minWidth: 70,
      width: 80,
      filter: false,
      cellClass: 'text-center',
      cellRenderer: ({ data }: CellProps) =>
        data ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onOpenComments(data.raw)
            }}
            title="Комментарии к позиции"
            aria-label={`Комментарии к позиции ${data.partNumber}`}
            className={`text-sm hover:text-brand-700 ${
              data.commentsCount > 0 ? 'text-brand-600 font-medium' : 'text-gray-400'
            }`}
          >
            💬{data.commentsCount > 0 ? ` ${data.commentsCount}` : ''}
          </button>
        ) : null,
    },
  ]
}
